import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import api, { formatApiError } from '@/lib/api';
import { apartmentToForm, buildApartmentUpdatePayload } from '@/lib/apartmentPayload';
import { toast } from '@/components/ui/sonner';
import AdminPageShell from '@/components/admin/AdminPageShell';
import ApartmentAdminForm from '@/components/admin/ApartmentAdminForm';

export default function AdminApartmentEditPage() {
  const { aptId } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => apartmentToForm(null));
  const [propertyManagers, setPropertyManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');

  const load = useCallback(async () => {
    if (!aptId) return;
    setLoading(true);
    try {
      const [profileRes, pmRes] = await Promise.all([
        api.get(`/apartments/${aptId}`),
        api.get('/property-managers'),
      ]);
      const apt = profileRes.data?.apartment;
      if (!apt) {
        toast.error('Apartment not found');
        navigate('/admin/apartments');
        return;
      }
      setTitle(apt.title || '');
      setForm(apartmentToForm(apt));
      setPropertyManagers(pmRes.data || []);
    } catch {
      toast.error('Failed to load apartment');
      navigate('/admin/apartments');
    } finally {
      setLoading(false);
    }
  }, [aptId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.address.trim()) {
      toast.error('Title and address are required');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/apartments/${aptId}`, buildApartmentUpdatePayload(form));
      toast.success(t('admin.apt.updated'));
      navigate(`/admin/apartments/${aptId}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminPageShell testId="admin-apartment-edit-page" maxWidth="max-w-2xl">
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#0B3D2E] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      testId="admin-apartment-edit-page"
      maxWidth="max-w-2xl"
      title={t('admin.apt.editPage')}
      breadcrumbs={[
        { label: t('admin.apartments'), to: '/admin/apartments' },
        { label: title, to: `/admin/apartments/${aptId}` },
        { label: t('admin.apt.editPage') },
      ]}
    >
      <ApartmentAdminForm
        form={form}
        setForm={setForm}
        propertyManagers={propertyManagers}
        saving={saving}
        onCancel={() => navigate(`/admin/apartments/${aptId}`)}
        onSave={handleSave}
        saveLabelKey="common.save"
      />
    </AdminPageShell>
  );
}
